import type { TFunction } from "i18next";
import { isApiError } from "../../api/client.js";

const CAMPAIGN_ERROR_KEYS: Record<string, string> = {
  CAMPAIGN_AUDIENCE_SNAPSHOT_STALE: "campaigns.errors.snapshotStale",
  ORGANIZATION_HAS_NO_ACTIVE_OWNER: "organization.platformAdmin.noActiveOwner",
  ORGANIZATION_SUSPENDED: "organization.suspended.outboundBlocked",
  CAMPAIGN_AUDIENCE_EMPTY: "campaigns.errors.emptyAudience",
  CAMPAIGN_NOT_DRAFT: "campaigns.errors.notDraft",
  CAMPAIGN_AUDIENCE_UPLOAD_NOT_FOUND: "campaigns.errors.uploadNotFound",
  CAMPAIGN_AUDIENCE_UPLOAD_NOT_READY: "campaigns.errors.uploadNotReady",
  CAMPAIGN_AUDIENCE_UPLOAD_EXPIRED: "campaigns.errors.uploadExpired",
  CAMPAIGN_AUDIENCE_UPLOAD_CONSUMED: "campaigns.errors.uploadConsumed",
  CAMPAIGN_AUDIENCE_FILE_TOO_LARGE: "campaigns.errors.fileTooLarge",
  CAMPAIGN_AUDIENCE_INVALID_FILE_TYPE: "campaigns.errors.invalidFileType",
  CAMPAIGN_AUDIENCE_WORKSHEET_UNKNOWN: "campaigns.errors.worksheetUnknown",
  CAMPAIGN_AUDIENCE_ROW_LIMIT_EXCEEDED: "campaigns.errors.rowLimit",
  CAMPAIGN_AUDIENCE_COLUMN_LIMIT_EXCEEDED: "campaigns.errors.columnLimit",
  CAMPAIGN_AUDIENCE_HEADER_INVALID: "campaigns.errors.headerInvalid",
  CAMPAIGN_AUDIENCE_HEADER_DUPLICATE: "campaigns.errors.headerDuplicate",
  CAMPAIGN_AUDIENCE_VALUE_TOO_LONG: "campaigns.errors.valueTooLong",
  CAMPAIGN_AUDIENCE_HEADERS_REQUIRED: "campaigns.errors.headersRequired",
  CAMPAIGN_AUDIENCE_HEADER_CONFIRMATION_REQUIRED: "campaigns.errors.headerConfirmation",
  CAMPAIGN_AUDIENCE_PHONE_COLUMN_REQUIRED: "campaigns.errors.phoneColumnRequired",
  CAMPAIGN_AUDIENCE_COMMIT_IN_PROGRESS: "campaigns.errors.commitInProgress",
  CAMPAIGN_AUDIENCE_IMPORT_FAILED: "campaigns.errors.importFailed",
  CAMPAIGN_AUDIENCE_COMMIT_FAILED: "campaigns.errors.commitFailed",
  CAMPAIGN_MESSAGE_INVALID_ENDPOINT: "campaigns.errors.messageInvalidEndpoint",
  CAMPAIGN_MESSAGE_INVALID_MAPPING: "campaigns.errors.messageInvalidMapping",
  CAMPAIGN_MESSAGE_UNKNOWN_UPLOAD_COLUMN: "campaigns.errors.messageUnknownColumn",
  CAMPAIGN_MESSAGE_TEMPLATE_NOT_ELIGIBLE: "campaigns.errors.messageTemplateNotEligible",
  CAMPAIGN_SEND_TEST_INVALID_DESTINATION: "campaigns.errors.sendTestInvalidDestination",
  CAMPAIGN_SEND_TEST_MISSING_VALUE: "campaigns.errors.sendTestMissingValue",
  CAMPAIGN_SEND_TEST_NOT_READY: "campaigns.errors.sendTestNotReady",
  CAMPAIGN_LAUNCH_NOT_READY: "campaigns.errors.launchNotReady",
  CAMPAIGN_NO_EXECUTABLE_RECIPIENTS: "campaigns.errors.noExecutableRecipients",
  CAMPAIGN_SCHEDULE_INVALID: "campaigns.errors.scheduleInvalid",
  CAMPAIGN_NOT_SCHEDULED: "campaigns.errors.notScheduled",
  CAMPAIGN_LAUNCH_CONFLICT: "campaigns.errors.launchConflict",
  STORAGE_QUOTA_EXCEEDED: "campaigns.errors.storageQuota",
  PLATFORM_NOT_ENABLED: "campaigns.errors.platformNotEnabled",
  FORBIDDEN: "campaigns.errors.forbidden",
  NOT_FOUND: "campaigns.errors.notFound",
  VALIDATION_FAILED: "campaigns.errors.validation",
  VALIDATION_ERROR: "campaigns.errors.validation",
};

export function mapCampaignError(t: TFunction, error: unknown, fallback: string): string {
  if (isApiError(error) && error.status === 0) {
    return t("campaigns.errors.unreachable");
  }
  if (isApiError(error) && error.code === "INSUFFICIENT_MESSAGE_CREDITS") {
    const available = error.details?.availableCredits;
    const required = error.details?.requiredCredits;
    if (available !== undefined && required !== undefined) {
      return t("campaigns.errors.insufficientCreditsWithCounts", {
        count: typeof required === "number" ? required : Number(required),
        available,
        required,
      });
    }
    return t("campaigns.errors.insufficientCredits");
  }
  const code = isApiError(error) ? error.code : undefined;
  const key = code ? CAMPAIGN_ERROR_KEYS[code] : undefined;
  if (key) return t(key);
  return fallback;
}

export function campaignFailureMessage(
  t: TFunction,
  failureCode: string | null,
): string {
  if (failureCode === "AUDIENCE_STALE") return t("campaigns.errors.failureAudienceStale");
  if (failureCode === "LAUNCH_NOT_READY") return t("campaigns.errors.failureLaunchNotReady");
  if (failureCode === "NO_EXECUTABLE_RECIPIENTS") return t("campaigns.errors.failureNoRecipients");
  return t("campaigns.errors.requestFailed");
}

export function campaignAudienceWorksheetNames(error: unknown): string[] | null {
  if (!isApiError(error) || error.code !== "CAMPAIGN_AUDIENCE_WORKSHEET_REQUIRED") return null;
  const raw = error.details?.worksheets;
  if (!raw) return null;
  const names = raw.split("\n").filter((name) => name.length > 0);
  return names.length > 1 ? names : null;
}

export function isCampaignSnapshotStaleError(error: unknown): boolean {
  return isApiError(error) && error.code === "CAMPAIGN_AUDIENCE_SNAPSHOT_STALE";
}

export function isCampaignUploadMissingError(error: unknown): boolean {
  return isApiError(error) && error.code === "CAMPAIGN_AUDIENCE_UPLOAD_NOT_FOUND";
}

export function isCampaignEmptyAudienceError(error: unknown): boolean {
  return isApiError(error) && error.code === "CAMPAIGN_AUDIENCE_EMPTY";
}
